import { Box, Card, Chip, Divider, Grid, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../app/store/configureStore';
import { Order } from '../../../app/data/models/orders/order';
import { getCustomerOrders } from './customerOrdersSlice';
import { useNavigate, useParams } from 'react-router-dom';
import OrderStatusLabel from '../../../app/components/OrderStatusLabel';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../app/utils/formatters';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import { getOrderTypeChipProps } from '../../../app/utils/orderUtils';
import ListTitleBox from '../../../app/components/list/ListTitleBox';

const CustomerOrderPage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orderId } = useParams<{
    orderId: string;
  }>();
  const { orders } = useAppSelector((state) => state.customerOrders);
  const [order, setOrder] = useState<Order | undefined>(undefined);

  useEffect(() => {
    dispatch(getCustomerOrders());
  }, [dispatch]);

  useEffect(() => {
    setOrder(orders.find((item) => item.id === orderId));
  }, [orders, orderId]);

  const handleBackClick = () => {
    navigate('/customer/orders');
  };

  if (!order)
    return <Typography variant="h4">{t('orders.list.empty')}</Typography>;

  return (
    <Box sx={{ width: '100%', mx: 'auto' }}>
      <ListTitleBox
        onBackClick={handleBackClick}
        title={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              flexWrap: 'wrap',
              gap: { xs: 1, sm: 2 },
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
              }}
            >
              #{order.tokenNumber}
            </Typography>
            {order.restaurantName && (
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{
                  fontWeight: 500,
                }}
              >
                {order.restaurantName}
              </Typography>
            )}
          </Box>
        }
      />
      {/* Order Header */}
      <Card sx={{ mb: 3, p: 2, borderRadius: 2 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          flexWrap="wrap"
          gap={2}
          mb={2}
        >
          <Box>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Chip
                icon={<AccessTimeIcon />}
                label={new Date(order.orderDate).toLocaleString()}
                variant="outlined"
              />
              <Chip
                icon={getOrderTypeChipProps(order.orderType).icon}
                color={getOrderTypeChipProps(order.orderType).color}
                label={t(getOrderTypeChipProps(order.orderType).label)}
                variant="outlined"
              />
              {order.tableNumber && (
                <Chip
                  icon={<TableRestaurantIcon />}
                  label={`${t('orders.details.table')} ${order.tableNumber}`}
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
          <OrderStatusLabel orderStatus={order.orderStatus} />
        </Box>

        {order.notes && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <StickyNote2Icon fontSize="small" />
              {order.notes}
            </Typography>
          </Box>
        )}
      </Card>

      {/* Order Items */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('orders.details.items')}
          </Typography>

          <Box>
            {order.subOrders.flatMap((subOrder) =>
              subOrder.orderItems.map((item, index, array) => (
                <Box key={item.id}>
                  <Box sx={{ py: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={8}>
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 500 }}
                          >
                            {item.quantity}x {item.name}
                          </Typography>
                          {item.customizations?.map((customization) => (
                            <Box key={customization.id} sx={{ mt: 1, pl: 2 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {customization.customerInstruction}:
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ pl: 1 }}
                              >
                                {customization.options
                                  .filter((option) => option.quantity > 0)
                                  .map((option, index, array) => (
                                    <span key={option.id}>
                                      {option.quantity}x {option.name}
                                      {option.price > 0 &&
                                        ` (+${formatCurrency(option.price)})`}
                                      {index < array.length - 1 ? ', ' : ''}
                                    </span>
                                  ))}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box display="flex" justifyContent="flex-end">
                          <Typography>
                            {formatCurrency(item.price * item.quantity)}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  {index < array.length - 1 && <Divider />}
                </Box>
              ))
            )}
          </Box>
        </Box>
      </Card>

      {/* Order Summary */}
      <Card sx={{ borderRadius: 2 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            {t('orders.details.summary')}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="body1">
                {t('orders.details.subtotal')}
              </Typography>
              <Typography>{formatCurrency(order.subtotal)}</Typography>
            </Box>
            {/* TODO: Implement tax calculation
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="body1">{t('orders.details.tax')}</Typography>
              <Typography>{formatCurrency(order.total * 0.1)}</Typography>
            </Box> */}
            <Divider sx={{ my: 2 }} />
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">{t('orders.details.total')}</Typography>
              <Typography variant="h6">
                {formatCurrency(order.total)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

export default CustomerOrderPage;
